import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import omanMapDark from '../assets/why-oman/oman-map-transparent.png';
import iconFz from '../assets/why-oman/icons/fz.png';
import iconIz from '../assets/why-oman/icons/iz.png';
import iconEz from '../assets/why-oman/icons/ez.png';
import iconIt from '../assets/why-oman/icons/it.png';
import { INDUSTRIAL_LOGO_URI } from '../assets/mapimages/industrialLogoUri.js';
import markerPopupImg4 from '../assets/mapimages/icon4image.png';
import markerPopupImg5 from '../assets/mapimages/icon5image.png';
import markerPopupImg7 from '../assets/mapimages/icon7image.png';
import markerPopupImg12 from '../assets/mapimages/icon12image.jpg';
import markerPopupImg13 from '../assets/mapimages/icon13image.jpeg';
import markerPopupImg15 from '../assets/mapimages/icon15image.jpg';

const MARKER_HEAD_IMAGES = {
  '4': markerPopupImg4,
  '5': markerPopupImg5,
  '7': markerPopupImg7,
  '12': markerPopupImg12,
  '13': markerPopupImg13,
  '15': markerPopupImg15
};

const TAB_DEFS = [
  { id: 'overview', types: null },
  { id: 'industrial', types: ['IZ'] },
  { id: 'economicFree', types: ['EZ', 'FZ'] },
  { id: 'tech', types: ['IT'] }
];

const TYPE_ICONS = {
  FZ: iconFz,
  IZ: iconIz,
  EZ: iconEz,
  IT: iconIt
};

const UNCLICKABLE_IDS = new Set(['1', '2', '3', '6', '8', '9', '10', '11', '14']);

const OMAN_MARKERS = [
  { id: '1', num: 1, type: 'IZ', left: '55%', top: '5%', logo: null },
  { id: '2', num: 2, type: 'IZ', left: '50%', top: '22%', logo: null },
  { id: '3', num: 3, type: 'IZ', left: '57%', top: '20%', logo: null },
  { id: '4', num: 4, type: 'FZ', left: '59%', top: '22%', logo: null },
  { id: '5', num: 5, type: 'EZ', left: '73%', top: '29%', logo: null },
  { id: '6', num: 6, type: 'IZ', left: '79%', top: '29%', logo: null },
  { id: '7', num: 7, type: 'IT', left: '82%', top: '29%', logo: null },
  { id: '8', num: 8, type: 'IZ', left: '51%', top: '35%', logo: null },
  { id: '9', num: 9, type: 'IZ', left: '74%', top: '35%', logo: null },
  { id: '10', num: 10, type: 'IZ', left: '64%', top: '43%', logo: null },
  { id: '11', num: 11, type: 'IZ', left: '93%', top: '40%', logo: null },
  { id: '12', num: 12, type: 'FZ', left: '71%', top: '72%', logo: null },
  { id: '13', num: 13, type: 'FZ', left: '10%', top: '90%', logo: null },
  { id: '14', num: 14, type: 'IZ', left: '30%', top: '95%', logo: null },
  { id: '15', num: 15, type: 'FZ', left: '33%', top: '95%', logo: null }
];

const IndustrialStatArrow = () => (
  <svg
    className="shrink-0"
    width="24"
    height="30"
    viewBox="0 0 24 30"
    fill="none"
    aria-hidden
  >
    <path d="M12 0L0 14h7v16h10V14h7L12 0z" fill="#004057" />
  </svg>
);

const INDUSTRIAL_STATS = [
  {
    labelKey: 'website.whyOmanMap.industrialPanel.stats.investors',
    current: { year: '2020', value: '+2,200' },
    previous: { year: '2008', value: '705' }
  },
  {
    labelKey: 'website.whyOmanMap.industrialPanel.stats.employees',
    current: { year: '2020', value: '+53K' },
    previous: { year: '2008', value: '21.8K' }
  },
  {
    labelKey: 'website.whyOmanMap.industrialPanel.stats.investments',
    current: { year: '2020', value: '$18bn' },
    previous: { year: '2008', value: '$7.8bn' }
  }
];

const markerName = (t, id) => t(`website.whyOmanMap.markers.${id}.name`);
const markerHighlight = (t, id) => t(`website.whyOmanMap.markers.${id}.highlight`);
const typeLabel = (t, type) => t(`website.whyOmanMap.types.${type}`);

/** emphasizedTypes: null = all rows full opacity (overview); array = dim non-matching types */
const ZoneTypeLegend = ({ emphasizedTypes }) => {
  const { t } = useLanguage();
  return (
    <div className="space-y-4">
      {['FZ', 'IZ', 'EZ', 'IT'].map((type) => {
        const icon = TYPE_ICONS[type];
        const isEmphasized = emphasizedTypes === null || emphasizedTypes.includes(type);
        return (
          <div
            key={type}
            className={`flex items-center gap-3 transition-opacity ${isEmphasized ? 'opacity-100' : 'opacity-40'}`}
          >
            <span className="w-8 h-8">
              <img src={icon} alt={typeLabel(t, type)} className="w-8 h-8" />
            </span>
            <span className="text-[#0F5A7B] text-base md:text-lg">{typeLabel(t, type)}</span>
          </div>
        );
      })}
    </div>
  );
};

const OverviewMapLeftText = () => {
  const { t } = useLanguage();
  return (
    <div
      className="inline-flex w-full flex-col items-start gap-[11px] text-start"
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      <div className="flex w-full min-w-0 flex-row flex-nowrap items-baseline gap-x-2 text-start sm:gap-x-3">
        <span className="shrink-0 text-[#005471] text-[clamp(1.5rem,6.5vw,4rem)] font-extrabold leading-none md:leading-[1.05] md:text-[clamp(2rem,9vw,4.5rem)] lg:text-[52px] lg:leading-[1.05] xl:text-[60px] xl:leading-none">
          {t('website.whyOmanMap.overviewKpiValue')}
        </span>
        <span className="min-w-0 text-[#005471] text-[clamp(0.875rem,3.25vw,2.25rem)] font-extrabold leading-tight md:leading-[1.15] md:text-[clamp(1.05rem,4.5vw,2.5rem)] lg:text-[28px] lg:leading-tight xl:text-[32px]">
          {t('website.whyOmanMap.overviewKpiUnit')}
        </span>
      </div>
      <div className="w-full text-start text-[#006D92] text-[clamp(1rem,4vw,2.5rem)] font-extrabold leading-snug md:leading-[60px] lg:text-[26px] lg:leading-snug xl:text-[30px]">
        {t('website.whyOmanMap.overviewHeadline')}
      </div>
    </div>
  );
};

const IndustrialInfoPanel = () => {
  const { t } = useLanguage();
  const objectives = [
    t('website.whyOmanMap.industrialPanel.objective1'),
    t('website.whyOmanMap.industrialPanel.objective2'),
    t('website.whyOmanMap.industrialPanel.objective3'),
    t('website.whyOmanMap.industrialPanel.objective4')
  ];
  return (
    <div className="flex flex-col gap-6 text-start" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div>
        <img
          src={INDUSTRIAL_LOGO_URI}
          alt={t('website.whyOmanMap.industrialPanel.logoAlt')}
          className="h-auto max-w-[186px] object-contain"
        />
      </div>

      <div>
        <h4 className="text-[#005471] text-2xl font-extrabold leading-tight">
          {t('website.whyOmanMap.industrialPanel.aboutTitle')}
        </h4>
        <p className="mt-1 text-[#004057] text-base leading-6">{t('website.whyOmanMap.industrialPanel.aboutBody')}</p>
      </div>

      <div>
        <h4 className="text-[#005471] text-2xl font-extrabold leading-tight">
          {t('website.whyOmanMap.industrialPanel.objectivesTitle')}
        </h4>
        <ul className="mt-2 list-disc space-y-1 ps-5 text-[#004057] text-base leading-6 marker:text-[#005471]">
          {objectives.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {INDUSTRIAL_STATS.slice(0, 2).map((stat) => (
          <div
            key={stat.labelKey}
            className="flex flex-col items-stretch justify-start rounded-xl px-3 py-4 text-start"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.60) 0%, rgba(255,255,255,0.20) 100%)',
              boxShadow: '0px 12px 24px rgba(0,0,0,0.05)',
              outline: '0.58px rgba(138,208,232,0.60) solid',
              outlineOffset: '-0.58px',
              backdropFilter: 'blur(11.6px)'
            }}
          >
            <div className="flex w-full items-start gap-2 border-b border-[#54BBDE] pb-3">
              <IndustrialStatArrow />
              <div className="flex min-w-0 flex-1 flex-col text-start">
                <span className="text-xs font-bold text-[#008BBB]">{stat.current.year}</span>
                <span className="text-lg font-bold leading-tight text-[#004057]">{stat.current.value}</span>
                <span className="mt-1 text-xs font-bold text-[#54BBDE]">{stat.previous.year}</span>
                <span className="text-lg font-bold leading-tight text-[#006D92]">{stat.previous.value}</span>
              </div>
            </div>
            <p className="mt-2 text-start text-sm font-semibold text-[#005471]">{t(stat.labelKey)}</p>
          </div>
        ))}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-3">
        <div
          className="flex flex-col items-stretch justify-start rounded-xl px-3 py-4 text-start"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.60) 0%, rgba(255,255,255,0.20) 100%)',
            boxShadow: '0px 12px 24px rgba(0,0,0,0.05)',
            outline: '0.58px rgba(138,208,232,0.60) solid',
            outlineOffset: '-0.58px',
            backdropFilter: 'blur(11.6px)'
          }}
        >
          <div className="flex w-full items-start gap-2 border-b border-[#54BBDE] pb-3">
            <IndustrialStatArrow />
            <div className="flex min-w-0 flex-1 flex-col text-start">
              <span className="text-xs font-bold text-[#008BBB]">{INDUSTRIAL_STATS[2].current.year}</span>
              <span className="text-lg font-bold leading-tight text-[#004057]">{INDUSTRIAL_STATS[2].current.value}</span>
              <span className="mt-1 text-xs font-bold text-[#54BBDE]">{INDUSTRIAL_STATS[2].previous.year}</span>
              <span className="text-lg font-bold leading-tight text-[#006D92]">{INDUSTRIAL_STATS[2].previous.value}</span>
            </div>
          </div>
          <p className="mt-2 text-start text-sm font-semibold text-[#005471]">{t(INDUSTRIAL_STATS[2].labelKey)}</p>
        </div>
      </div>
    </div>
  );
};

const OmanMapSvg = ({ activeMarkerId, onSelectMarker, markers, mapAlt, t }) => {
  return (
    <div className="relative w-full max-w-[320px] sm:max-w-[440px] md:max-w-[520px] lg:ml-auto lg:w-full lg:max-w-none xl:max-w-none">
      <img
        src={omanMapDark}
        alt={mapAlt}
        className="w-full h-auto max-h-[55vh] lg:max-h-[min(82vh,920px)] xl:max-h-[min(88vh,1000px)] 2xl:max-h-[min(90vh,1080px)] object-contain select-none pointer-events-none"
        loading="lazy"
      />

      {markers.map((marker) => {
        const icon = TYPE_ICONS[marker.type] || TYPE_ICONS.FZ;
        const isActive = activeMarkerId === marker.id;
        const isClickable = !UNCLICKABLE_IDS.has(marker.id);
        const name = markerName(t, marker.id);
        const typeStr = typeLabel(t, marker.type);

        return (
          <button
            type="button"
            key={marker.id}
            onClick={() => isClickable && onSelectMarker(marker.id)}
            className={`absolute -translate-x-1/2 -translate-y-1/2 h-4 w-4 rounded-full shadow-sm transition-transform sm:h-5 sm:w-5 lg:h-[18px] lg:w-[18px] ${
              isActive ? 'scale-110 ring-1 ring-white' : 'hover:scale-105'
            } ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
            style={{ left: marker.left, top: marker.top, opacity: isClickable ? 1 : 0.7 }}
            aria-label={
              isClickable ? t('website.whyOmanMap.showDetailsFor', { name }) : t('website.whyOmanMap.markerLocation', { name })
            }
          >
            <img
              src={icon}
              alt={typeStr}
              className="h-4 w-4 select-none pointer-events-none sm:h-5 sm:w-5 lg:h-[18px] lg:w-[18px]"
            />
          </button>
        );
      })}
    </div>
  );
};

const WhyOmanMap = () => {
  const { t } = useLanguage();
  const [activeTabId, setActiveTabId] = useState('overview');

  const activeTab = TAB_DEFS.find((tab) => tab.id === activeTabId) || TAB_DEFS[0];
  const visibleMarkers =
    activeTabId === 'overview'
      ? OMAN_MARKERS
      : OMAN_MARKERS.filter((m) => (activeTab.types || []).includes(m.type));

  const [activeMarkerId, setActiveMarkerId] = useState(visibleMarkers[0]?.id || OMAN_MARKERS[0].id);
  const [popupMarkerId, setPopupMarkerId] = useState(null);

  const safeActiveMarkerId = visibleMarkers.some((m) => m.id === activeMarkerId)
    ? activeMarkerId
    : (visibleMarkers[0]?.id || OMAN_MARKERS[0].id);

  const showIndustrialPanel = activeTabId === 'industrial';
  const mapAlt = t('website.whyOmanMap.mapAlt');
  const zoneLegendEmphasizedTypes =
    activeTabId === 'overview' ? null : (activeTab.types || []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className="mt-0"
    >
      <div className="rounded-3xl border border-[#C7E3EA] bg-[#EAF6FA] p-5 md:p-8">
        <h3 className="text-[#0D4D6D] text-3xl md:text-5xl font-bold tracking-tight">
          {t('website.whyOmanMap.sectionTitle')}
        </h3>

        <p className="mt-3 text-[#1E6987] text-sm md:text-xl max-w-5xl">
          {t('website.whyOmanMap.sectionSubtitle')}
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          {TAB_DEFS.map((tab) => {
            const isActive = tab.id === activeTabId;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTabId(tab.id);
                  const nextVisible =
                    tab.id === 'overview'
                      ? OMAN_MARKERS
                      : OMAN_MARKERS.filter((m) => (tab.types || []).includes(m.type));
                  if (nextVisible.length > 0) {
                    setActiveMarkerId(nextVisible[0].id);
                  }
                }}
                className={`px-6 py-2 rounded-full text-sm transition-colors ${
                  isActive
                    ? 'bg-[#0F4D7A] text-white font-semibold'
                    : 'border border-[#1A6E8F] text-[#104E70] font-medium bg-white/40 hover:bg-white/60'
                }`}
              >
                {t(`website.whyOmanMap.tabs.${tab.id}`)}
              </button>
            );
          })}
        </div>

        <div className="mt-6 md:mt-8 rounded-3xl bg-gradient-to-b from-[#B6DDE8] to-[#8CC6D8] p-4 sm:p-6 md:p-8 lg:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] gap-6 lg:gap-6 xl:gap-8 items-start text-start">
            <div className="order-2 lg:order-1 text-start">
              {showIndustrialPanel ? (
                <IndustrialInfoPanel />
              ) : activeTabId === 'overview' ? (
                <>
                  <OverviewMapLeftText />
                  <p className="mt-6 sm:mt-8 text-[#0A4B6A] text-2xl sm:text-3xl md:text-4xl lg:text-3xl xl:text-4xl font-bold">
                    {t('website.whyOmanMap.overviewSecondaryNumber')}{' '}
                    <span className="text-white">{t('website.whyOmanMap.overviewSecondaryHighlight')}</span>
                  </p>
                  <div className="mt-6 sm:mt-8 hidden lg:block">
                    <ZoneTypeLegend emphasizedTypes={null} />
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[#0A4B6A] text-4xl sm:text-5xl md:text-7xl lg:text-6xl xl:text-7xl font-bold leading-none">
                    {t(`website.whyOmanMap.left.${activeTabId}.kpiValue`)}
                  </p>
                  <p className="mt-1 sm:mt-2 text-[#0A4B6A] text-2xl sm:text-3xl md:text-5xl lg:text-4xl xl:text-5xl font-semibold">
                    {t(`website.whyOmanMap.left.${activeTabId}.kpiUnit`)}
                  </p>
                  <p className="mt-3 sm:mt-4 text-[#0D5678] text-xl sm:text-2xl md:text-4xl lg:text-3xl xl:text-4xl font-bold leading-tight">
                    {t(`website.whyOmanMap.left.${activeTabId}.headlineLine1`)}
                    <br />
                    {t(`website.whyOmanMap.left.${activeTabId}.headlineLine2`)}
                  </p>

                  {(() => {
                    const secondaryPrefix = t(`website.whyOmanMap.left.${activeTabId}.secondaryPrefix`);
                    const secondaryHighlight = t(`website.whyOmanMap.left.${activeTabId}.secondaryHighlight`);
                    if (!secondaryPrefix && !secondaryHighlight) return null;
                    return (
                      <p className="mt-6 sm:mt-8 text-[#0A4B6A] text-2xl sm:text-3xl md:text-5xl lg:text-4xl xl:text-5xl font-bold">
                        {secondaryPrefix}
                        {secondaryPrefix && secondaryHighlight ? ' ' : ''}
                        {secondaryHighlight ? <span className="text-white">{secondaryHighlight}</span> : null}
                      </p>
                    );
                  })()}

                  <div className="mt-6 sm:mt-8 hidden lg:block">
                    <ZoneTypeLegend emphasizedTypes={activeTab.types || []} />
                  </div>
                </>
              )}
            </div>

            <div className="order-1 lg:order-2 flex flex-col items-start gap-5 lg:gap-4">
              <OmanMapSvg
                activeMarkerId={safeActiveMarkerId}
                onSelectMarker={(id) => {
                  setActiveMarkerId(id);
                  setPopupMarkerId(id);
                }}
                markers={visibleMarkers}
                mapAlt={mapAlt}
                t={t}
              />
            </div>

            {!showIndustrialPanel && (
              <div className="order-3 block w-full lg:hidden">
                <div className="mt-2 sm:mt-4">
                  <ZoneTypeLegend emphasizedTypes={zoneLegendEmphasizedTypes} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {popupMarkerId && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={() => setPopupMarkerId(null)}
          >
            <motion.div
              key={popupMarkerId}
              className="w-full max-w-md overflow-hidden rounded-2xl bg-white/95 border border-white shadow-xl text-start"
              initial={{ opacity: 0, scale: 0.94, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              {(() => {
                const marker = OMAN_MARKERS.find((m) => m.id === popupMarkerId) || null;
                if (!marker) return null;
                const headImage = MARKER_HEAD_IMAGES[marker.id];
                const name = markerName(t, marker.id);
                const highlight = markerHighlight(t, marker.id);
                const zoneTypeLabel = typeLabel(t, marker.type);

                return (
                  <>
                    {headImage && (
                      <div className="w-full overflow-hidden border-b border-slate-100 bg-slate-50">
                        <img
                          src={headImage}
                          alt=""
                          className="h-auto w-full max-h-52 object-cover object-center"
                        />
                      </div>
                    )}

                    <div className="p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 text-start">
                          <p className="text-xs font-semibold uppercase tracking-wider text-[#7D32EA]">
                            {zoneTypeLabel}
                          </p>
                          <h4 className="mt-1 text-[#0D4D6D] text-lg md:text-xl font-bold">{name}</h4>
                        </div>
                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white px-3 py-1 text-sm text-slate-700 hover:bg-slate-50"
                          onClick={() => setPopupMarkerId(null)}
                          aria-label={t('website.whyOmanMap.closeAria')}
                        >
                          ✕
                        </button>
                      </div>

                      {marker.logo && (
                        <div className="mt-3">
                          <img src={marker.logo} alt={`${name} logo`} className="h-14 w-auto object-contain" />
                        </div>
                      )}

                      <p className="mt-3 text-start text-[#185E7D] text-sm leading-relaxed">{highlight}</p>
                    </div>
                  </>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default WhyOmanMap;
